import toBooleanString from "~/kernel/form/toBooleanString";
import route from "./route";
import type {
  Booking,
  BookingEvaluation,
  Company,
  Customer,
  DayNote,
  ImageRelation,
  Journal,
  Label,
  PriceAdjustment,
  SendOut,
} from "@prisma/client";
import type { Presence } from "~/repositories/Presence";
import formatUrlDate from "~/kernel/dateTime/formatUrlDate";
import buildParams from "~/kernel/searchParams/buildParams";
import getCurrentDate from "~/kernel/dateTime/getCurrentDate";
import type BookingStatus from "~/models/booking/BookingStatus";
import startOf from "~/kernel/dateTime/startOf";
import JournalTab from "~/routes/customer-animals.$id.journal/JournalTab";
import CustomersTab from "~/models/customer/CustomersTab";
import type LatestBookingFilter from "~/routes/journals/LatestBookingFilter";
import type UpcomingBookingFilter from "~/routes/journals/UpcomingBookingFilter";
import { z } from "zod";
import type { CustomerAnimal } from "~/repositories/CustomerAnimal";
import CHECKBOX_FALSE_VALUE from "~/constants/form/CHECKBOX_FALSE_VALUE";
import CHECKBOX_TRUE_VALUE from "~/constants/form/CHECKBOX_TRUE_VALUE";
import { ImporterType } from "~/routes/companies.$id.customers.imports.$type.new/ImporterType";

const LABEL_ROUTES = {
  createLabel: route("/labels/create"),
  labels: route("/labels"),
  label: route(({ id }: Pick<Label, "id">) => `/labels/${id}`),
};

const ADD_ON_SERVICE_ROUTES = {
  new: route.fromTemplate("/add-on-services/new"),
  edit: route.fromTemplate("/add-on-services/:id/edit", {
    params: z.object({ id: z.string() }),
  }),
  list: route.fromTemplate("/add-on-services"),
  editSortOrder: route.fromTemplate("/add-on-services/sort-order/edit"),
};

const SEND_OUT_ROUTES = {
  sendOuts: route(({ page = 1 } = {}) => `/send-outs${buildParams({ page })}`),
  newSendOut: route("/send-outs/new"),
  editSendOut: route(({ id }: Pick<SendOut, "id">) => `/send-outs/${id}/edit`),
  sendOutStatus: route(
    ({ id }: Pick<SendOut, "id">) => `/send-outs/${id}/status`,
  ),
};

const STAMP_CARD_ROUTES = {
  index: route.fromTemplate("/company-settings/stamp-cards"),
  new: route.fromTemplate("/company-settings/stamp-cards/new"),
  edit: route.fromTemplate("/company-settings/stamp-cards/:id/edit", {
    params: z.object({ id: z.string() }),
  }),
};

const CUSTOMER_STAMP_CARD_ROUTES = {
  consumeStamps: route.fromTemplate(
    "/company/customers/:customerId/stamp-cards/:stampCardId/consume-stamps",
    {
      params: z.object({ customerId: z.string(), stampCardId: z.string() }),
    },
  ),
  undoStamp: route.fromTemplate(
    "/company/customers/:customerId/stamp-cards/:stampCardId/undo-stamp",
    {
      params: z.object({ customerId: z.string(), stampCardId: z.string() }),
    },
  ),
  stamp: route.fromTemplate(
    "/company/customers/:customerId/stamp-cards/:stampCardId/stamp",
    {
      params: z.object({ customerId: z.string(), stampCardId: z.string() }),
    },
  ),
  index: route.fromTemplate("/company/customers/:id/stamp-cards", {
    params: z.object({ id: z.string() }),
  }),
  edit: route.fromTemplate(
    "/company/customers/:customerId/stamp-cards/:id/edit",
    {
      params: z.object({ customerId: z.string(), id: z.string() }),
    },
  ),
};

const USER_ROUTES = {
  userUploadImage: route("/user/images/upload"),
};

const CUSTOMER_ANIMAL_ROUTES = {
  bookingEvaluationBookings: route(
    ({ id, page }: Pick<CustomerAnimal, "id"> & { page?: number }) =>
      `/customer-animals/${id}/booking-evaluations/bookings${buildParams({
        page,
      })}`,
  ),
  companyCreateAnimal: route("/customer-animals/create"),
  customerAnimalNewPriceAdjustment: route(
    ({ id }: Pick<CustomerAnimal, "id">) =>
      `/customer-animals/${id}/price-adjustments/new`,
  ),
  customerAnimals: route("/customer-animals"),
  companyNewAnimal: route(
    (
      props: {
        customerId?: string;
        redirectUrl?: string;
        tab?: CustomersTab;
      } = {},
    ) => `/customer-animals/new${buildParams(props)}`,
  ),
  editCustomerAnimalOwnerships: route(
    ({ id }: Pick<CustomerAnimal, "id">) =>
      `/customer-animals/${id}/ownerships/edit`,
  ),
  linkAnimal: route.fromTemplate(`/customer-animals/:id/link-animal`, {
    params: z.object({ id: z.string() }),
  }),
  journal: route(
    ({
      id,
      tab = JournalTab.Animal,
      page,
    }: Pick<CustomerAnimal, "id"> & { tab?: JournalTab; page?: number }) =>
      `/customer-animals/${id}/journal/${tab}${buildParams({ page })}`,
  ),
  journalBookings: route(
    ({
      id,
      page,
      status,
    }: Pick<CustomerAnimal, "id"> & {
      page?: number;
      status?: BookingStatus | null;
    }) =>
      `/customer-animals/${id}/journal/bookings` +
      buildParams({ page, status }),
  ),
  newBookingEvaluation: route(
    ({ id, bookingId }: Pick<CustomerAnimal, "id"> & { bookingId?: string }) =>
      `/customer-animals/${id}/booking-evaluations/new${buildParams({
        bookingId,
      })}`,
  ),
  updateCustomerAnimalLabel: route(
    ({ id, labelId }: { id: string; labelId: string }) =>
      `/customer-animals/${id}/labels/${labelId}/update`,
  ),
};

const SUBSCRIPTION_ROUTES = {
  companyResumeSubscription: route("/company/subscription/resume"),
};

const MEMBERSHIP_ROUTES = {
  editCompanyMemberships: route("/company-settings/memberships"),
  newCompanyMembership: route(
    ({ email }: { email?: string } = {}) =>
      `/company-settings/memberships/new${buildParams({ email })}`,
  ),
  companyMembershipInvite: route(
    ({ token }: { token: string }) => `/company-membership-invites/${token}`,
  ),
  companyMember: route(
    ({ userId }: { userId: string }) => `/company-settings/members/${userId}`,
  ),
};

const ACCOUNT_ROUTES = {
  companyEditAccount: route("/company-settings/account/edit"),
};

const INVOICE_ROUTES = {
  index: route.fromTemplate("/company-settings/subscription-invoices", {
    query: z.object({ page: z.coerce.number().catch(1) }),
  }),
};

const COMPANY_ROUTES = {
  companyInvoices: INVOICE_ROUTES,
  stampCards: STAMP_CARD_ROUTES,
  companyCustomerStampCards: CUSTOMER_STAMP_CARD_ROUTES,
  ...LABEL_ROUTES,
  ...CUSTOMER_ANIMAL_ROUTES,
  ...SEND_OUT_ROUTES,
  ...USER_ROUTES,
  ...SUBSCRIPTION_ROUTES,
  ...MEMBERSHIP_ROUTES,
  ...ACCOUNT_ROUTES,
  addOnServices: ADD_ON_SERVICE_ROUTES,
  companyHome: route("/company/home"),
  birthdayGreetings: route("/birthday-greetings"),
  bookingEvaluation: route(
    ({ id }: Pick<BookingEvaluation, "id">) => `/booking-evaluations/${id}`,
  ),
  bookingEvaluationBooking: route(
    ({ id }: Pick<Booking, "id">) => `/booking-evaluations/bookings/${id}`,
  ),
  defaultCancellationMessage: route(
    ({ bookingId }: { bookingId: string }) =>
      `/cancellation-message/${bookingId}/default`,
  ),
  clearCustomers: route(
    ({ tab }: { tab: CustomersTab }) =>
      `/company/customers/clear${buildParams({ tab })}`,
  ),
  companyBookingEstimatedTime: route("/company/bookings/estimated-time"),
  companyBookingRecommendedDates: route.fromTemplate(
    "/company/bookings/recommended-dates",
  ),
  companyBookings: route(
    (props: {
      day: Date | undefined | null;
      employeeId: string | null | undefined;
      view?: string | undefined;
    }) => {
      const { view, day, employeeId } = props;
      const params = buildParams({
        day: day && formatUrlDate(day),
        employeeId,
        view,
      });
      return `/company/bookings${params}`;
    },
  ),
  companyCancelBooking: route(
    ({ id }: Pick<Booking, "id">) => `/company/bookings/${id}/cancel`,
  ),
  companyCreateCustomer: route("/company/customers/create"),
  companyCustomer: route(
    ({ id }: { id: string }) =>
      `/company/customers/${id}?_data=routes/company.customers.$id`,
  ),
  companyCustomersJson: route(
    ({
      page,
      query,
      upcomingBooking,
      latestBooking,
      labelIds,
    }: {
      query?: string | null;
      page?: number;
      labelIds?: string[];
      upcomingBooking?: UpcomingBookingFilter;
      latestBooking?: LatestBookingFilter;
    }) =>
      `/company/customers/json${buildParams({
        page,
        query,
        latestBooking,
        upcomingBooking,
        labelIds,
      })}`,
  ),
  companyCustomersClaim: route(
    ({ slug }: Pick<Company, "slug">) => `/companies/${slug}/customers/claim`,
  ),
  companyPickCustomersImport: route.fromTemplate(
    "/companies/:id/customers/imports/type/pick",
    {
      query: z.object({
        tab: z.nativeEnum(CustomersTab),
      }),
      params: z.object({
        id: z.string(),
      }),
    },
  ),
  companyDayNote: route(
    ({ id }: Pick<DayNote, "id">) => `/company/day-notes/${id}`,
  ),
  companyEditBooking: route(
    ({ id }: Pick<Booking, "id">) => `/company/bookings/${id}/edit`,
  ),
  companyEditBookingDetails: route(
    ({ id }: Pick<Booking, "id">) => `/company/bookings/${id}/details/edit`,
  ),
  companyEditBookingSettings: route("/company-settings/booking-settings/edit"),
  companyEditCompanyInfo: route("/company-settings/company-info/edit"),
  companyEditContactDetails: route("/company-settings/contact-details/edit"),
  companyEditCustomer: route(
    (customer: Pick<Customer, "id">) =>
      `/company/customers/${customer.id}/edit`,
  ),
  companyEditDayNote: route(
    ({ id }: Pick<CustomerAnimal, "id">) => `/company/day-notes/${id}/edit`,
  ),
  companyEditNotificationSettings: route(
    "/company-settings/notification-settings/edit",
  ),
  companyEditOpeningHours: route("/company-settings/opening-hours/edit"),
  companyNewBooking: route(
    ({
      startsAt,
      employeeId,
      customerAnimalId,
    }: {
      startsAt?: Date;
      employeeId?: string | null;
      customerAnimalId?: string;
    } = {}) =>
      `/company/bookings/new${buildParams({
        customerAnimalId,
        employeeId,
        startsAt: formatUrlDate(startsAt ?? startOf(new Date(), "hour")),
      })}`,
  ),
  companyNewCustomer: route(
    (props: { tab: CustomersTab }) =>
      `/company/customers/new${buildParams(props)}`,
  ),
  companyNewDayNote: route(
    ({
      startsAt,
      employeeId,
      customerAnimalId,
    }: {
      startsAt?: Date;
      employeeId?: string | null;
      customerAnimalId?: string;
    } = {}) =>
      `/company/day-notes/new${buildParams({
        customerAnimalId,
        employeeId,
        startsAt: formatUrlDate(startsAt ?? startOf(new Date(), "hour")),
      })}`,
  ),
  companyQRCode: route(
    (company: Pick<Company, "slug">) => `/company-qr-code/${company.slug}`,
  ),
  companyReferrals: route("/company-settings/referrals"),
  companyTimePickerAvailableDays: route("/company/time-picker/available-days"),
  companyVerify: route("/company-settings/account/verify"),
  deleteAbsence: route((id: string, startDate: Date, redirectUrl: string) => {
    const params = buildParams({
      redirectUrl,
      startDate: formatUrlDate(startDate),
    });
    return `/company/bookings/absences/${id}/delete${params}`;
  }),
  deletePresence: route((id: string, startDate: Date, redirectUrl: string) => {
    const params = buildParams({
      redirectUrl,
      startDate: formatUrlDate(startDate),
    });
    return `/presences/${id}/delete${params}`;
  }),
  editAbsence: route(({ id }: Pick<Presence, "id">, redirectUrl) => {
    return `/company/bookings/absences/${id}/edit${buildParams({
      redirectUrl,
    })}`;
  }),
  editBookingEvaluation: route(
    ({ id }: Pick<BookingEvaluation, "id">) =>
      `/booking-evaluations/${id}/edit`,
  ),
  editCompanySubscription: route("/company-settings/subscription/edit"),
  editEmployee: route(({ id }: { id: string }) => `/employees/${id}/edit`),
  editPresence: route(({ id }: Pick<Presence, "id">, redirectUrl: string) => {
    return `/presences/${id}/edit${buildParams({ redirectUrl })}`;
  }),
  editPriceAdjustment: route(
    ({ id }: Pick<PriceAdjustment, "id">) => `/price-adjustments/${id}/edit`,
  ),
  editService: route((id: string) => `/services/${id}/edit`),
  editServicesSortOrder: route("/services/sort-order/edit"),
  editUserCompany: route("/company-settings"),
  employees: route("/employees"),
  fetchCompanyBookings: route(`/company/bookings?index`),
  getStartedTasks: route(`/get-started-tasks`),
  imageRelationImage: route(
    ({ id, imageId }: Pick<ImageRelation, "id" | "imageId">) =>
      `/image-relations/${id}/images/${imageId}`,
  ),
  journalImage: route(
    ({ id, journalId }: { id: string; journalId: string }) =>
      `/journals/${journalId}/journal-images/${id}`,
  ),
  journalImages: route(
    ({ id }: Pick<Journal, "id">) => `/journals/${id}/images`,
  ),
  journals: route(
    ({
      latestBooking = "all",
      page,
      query = "",
      tab = "animals",
      upcomingBooking = "all",
      labels = [],
    }: {
      query?: string | null;
      tab?: "animals" | "customers";
      page?: number;
      upcomingBooking?: string;
      latestBooking?: string;
      labels?: string[];
    } = {}) => {
      return `/journals${buildParams({
        latestBooking,
        page,
        query,
        tab,
        upcomingBooking,
        label: labels,
      })}`;
    },
  ),
  latestBirthdayGreetingMemory: route(
    ({ id }: Pick<CustomerAnimal, "id">) =>
      `/birthday-greetings/${id}/latest-memory`,
  ),
  markNotificationsAsRead: route("/notifications/mark-all-as-read"),
  memory: route((key: string) => `/memories/${key}`),
  newAbsence: route(
    ({
      startDate,
      startMinute,
      endMinute,
      employeeId,
      redirectUrl,
    }: {
      startDate?: Date;
      employeeId?: string | null;
      startMinute?: number;
      endMinute?: number;
      redirectUrl: string;
    }) => {
      const params = buildParams({
        employeeId,
        endMinute,
        redirectUrl,
        startDate: formatUrlDate(startDate ?? getCurrentDate()),
        startMinute,
      });
      return `/company/bookings/absences/new${params}`;
    },
  ),
  newEmployee: route("/employees/new"),
  newPresence: route(
    ({
      startDate,
      employeeId,
      startMinute,
      endMinute,
      redirectUrl,
    }: {
      redirectUrl?: string;
      startDate?: Date;
      startMinute?: number;
      endMinute?: number;
      employeeId?: string | null;
    } = {}) => {
      const params = buildParams({
        employeeId,
        endMinute,
        redirectUrl,
        startDate: formatUrlDate(startDate ?? getCurrentDate()),
        startMinute,
      });
      return `/presences/new${params}`;
    },
  ),
  newService: route("/services/new"),
  unsupportedCustomerImport: route(
    (props: { name: string; tab: CustomersTab }) =>
      `/unsupported-customer-import${buildParams(props)}`,
  ),
  newWavyCustomerImport: route(
    (props: { tab: CustomersTab }) =>
      `/wavy-customer-imports/new${buildParams(props)}`,
  ),
  newCustomerImport: route.fromTemplate(
    `/companies/:id/customers/imports/:type/new`,
    {
      params: z.object({
        id: z.string(),
        type: z.nativeEnum(ImporterType),
      }),
      query: z.object({ tab: z.nativeEnum(CustomersTab) }),
    },
  ),
  notifications: route.fromTemplate("/notifications", {
    query: z.object({ page: z.coerce.number().optional() }),
  }),
  presences: route(
    (props: {
      day: Date | undefined;
      employeeId: string | null | undefined;
    }) => {
      const { employeeId, day } = props;
      const params = buildParams({
        day: day && formatUrlDate(day),
        employeeId,
      });
      return `/presences${params}`;
    },
  ),
  companyServicesJson: route.fromTemplate("/company/services/json", {
    query: z.object({
      includeDeleted: z
        .union([
          z.literal(CHECKBOX_TRUE_VALUE),
          z.literal(CHECKBOX_FALSE_VALUE),
        ])
        .catch(CHECKBOX_FALSE_VALUE),
    }),
  }),
  services: route(
    (props?: { includeDeleted?: boolean }) =>
      `/services?index${
        props?.includeDeleted ? `&includeDeleted=${toBooleanString(true)}` : ""
      }`,
  ),
  timePickerEmployees: route("/company/time-picker-employees"),
};

export default COMPANY_ROUTES;
